// Formation steps: how a form is built from its root or stem, step by step, with the rule named.
// Deterministic and rule-based — the same intermediate stems the engine uses (पढा-, पढि-, पढे-, पढीअ-, पढिज्ज-, पढाव-).

import type {
    Case,
    Gender,
    KridantaKind,
    Lang,
    Num,
    Person,
    RootClass,
    Tense
} from '@/prakrit/lib/grammar/types'

import {
    addMatra,
    commonPrefix,
    displayEnding,
    lengthen,
    shorten
} from '@/prakrit/lib/grammar/devanagari'
import {
    CASE_LABEL,
    NUMBER_LABEL,
    PERSON_LABEL,
    TENSE_LABEL
} from '@/prakrit/lib/grammar/labels'

export type PartRole = 'base' | 'ending' | 'changed' | 'plus'

export interface Part {
    text: string
    role: PartRole
}

export interface FormationStep {
    parts: Part[]
    note: Lang
}

export interface Formation {
    result: string
    steps: FormationStep[]
    rule?: string
}

const COMBINING_START = /^[ऀ-ःऺ-ॏ॑-ॗॢॣ]/

/** A suffix ready to show on its own: ामि → आमि, ंति → ◌ंति. */
export const showEnding = (remainder: string): string => {
    if (!remainder) return ''

    const shown = displayEnding(remainder)

    if (COMBINING_START.test(shown)) return '◌' + shown

    return shown
}

interface Base {
    text: string
    note?: Lang
    rule?: string
    /** 'after': the ending triggers the change (पढ + मि → पढामि), so the ending is shown first. */
    order?: 'before' | 'after'
}

const plus = (a: string, b: string, roleA: PartRole = 'base'): Part[] => [
    { text: a, role: roleA },
    { text: ' + ', role: 'plus' },
    { text: b, role: 'ending' }
]

const ENDING_LABEL_OVERRIDE: Record<string, string> = { 'णीअ': 'अणीअ' }

const verbBases = (root: string, rootClass: RootClass, tense: Tense | `kr_${KridantaKind}`, person?: Person): Base[] => {
    if (rootClass === 'long') {
        const bases: Base[] = [{ text: root }]

        if (root === 'दा') bases.unshift({ text: 'दे', note: { hi: 'विकल्प: दा → दे', en: 'variant: दा becomes दे' }, rule: 'पाठ ८९' })

        return bases
    }

    const s = root
    const i = addMatra(s, 'इ')
    const e = addMatra(s, 'ए')
    const aa = addMatra(s, 'आ')
    const ii = addMatra(s, 'ई')
    const ia = ii + 'अ'
    const ijj = i + 'ज्ज'
    const pr = aa + 'व'
    const pre = aa + 'वे'
    const pri = aa + 'वि'

    const lengthenA: Base = { text: aa, note: { hi: 'मि / मो से पहले अ → आ', en: 'before मि / मो, अ becomes आ' }, rule: 'नि.१०', order: 'after' }
    const altE: Base = { text: e, note: { hi: 'विकल्प रूप: अ → ए', en: 'variant form: अ becomes ए' }, rule: 'पाठ ८९', order: 'after' }
    const futI: Base = { text: i, note: { hi: 'भविष्यत् में अ → इ', en: 'in the future, अ becomes इ' }, rule: 'नि.१३' }
    const toI: Base = { text: i, note: { hi: 'अ → इ', en: 'अ becomes इ' } }
    const toE: Base = { text: e, note: { hi: 'अ → ए', en: 'अ becomes ए' } }
    const passiveIa: Base = { text: ia, note: { hi: 'कर्मवाच्य: धातु + ईअ (अ → आ नहीं होता)', en: 'passive: root + ईअ (no lengthening)' }, rule: 'नि.८३' }
    const passiveIjj: Base = { text: ijj, note: { hi: 'कर्मवाच्य: धातु + इज्ज', en: 'passive: root + इज्ज' }, rule: 'नि.८३' }
    const causative: Base = { text: pr, note: { hi: 'प्रेरणार्थक: धातु + आव', en: 'causative: root + आव' }, rule: 'नि.९५' }
    const causativeE: Base = { text: pre, note: { hi: 'प्रेरणार्थक विकल्प: धातु + आवे', en: 'causative variant: root + आवे' }, rule: 'पाठ ८१' }
    const causativeI: Base = { text: pri, note: { hi: 'प्रेरणार्थक: धातु + आव, फिर अ → इ', en: 'causative: root + आव, then अ becomes इ' }, rule: 'नि.९५' }
    const plain: Base = { text: s }

    switch (tense) {
        case 'vartaman':
            return person === 'uttam' ? [lengthenA, altE] : [altE, plain]
        case 'bhoot':
            return [plain]
        case 'bhavishya':
            return [futI]
        case 'vidhi':
            return [plain]
        case 'karmani_vartaman':
        case 'karmani_bhoot':
        case 'karmani_vidhi':
            return [passiveIa, passiveIjj]
        case 'prerak_vartaman':
            return [causativeE, causative]
        case 'prerak_bhoot':
        case 'prerak_vidhi':
            return [causative]
        case 'prerak_bhavishya':
            return [causativeI]
        case 'kriyatipatti':
            return [toE, plain]
        case 'kr_sambandh':
        case 'kr_hetvarth':
        case 'kr_bhoot':
        case 'kr_bhavishya':
        case 'kr_karmani_bhavishya':
            return [toI]
        case 'kr_vartaman':
            return [plain]
        case 'kr_vidhi':
            return [toE, plain]
        case 'kr_karmani_vartaman':
            return [passiveIa]
        case 'kr_prerak_sambandh':
        case 'kr_prerak_hetvarth':
        case 'kr_prerak_bhoot':
            return [causativeI]
        case 'kr_prerak_vidhi':
            return [causativeE, causative]
        case 'kr_prerak_vartaman':
            return [causative]
        default:
            return [plain]
    }
}

const build = (start: string, form: string, bases: Base[], endingNote: Lang, rule?: string): Formation => {
    const base = bases.find(b => form.startsWith(b.text)) ?? { text: start }
    const remainder = form.slice(base.text.length)
    const ending = ENDING_LABEL_OVERRIDE[remainder] ?? showEnding(remainder)
    const steps: FormationStep[] = [{ parts: [{ text: start, role: 'base' }], note: { hi: 'धातु', en: 'the root' } }]
    const bare: FormationStep = { parts: [{ text: base.text, role: 'base' }], note: { hi: 'बिना प्रत्यय, मूल धातु ही (विकल्प)', en: 'no ending: the bare root (variant)' } }
    const changed = base.text !== start && base.note

    if (changed && base.order === 'after' && ending) {
        steps.push({ parts: plus(start, ending), note: endingNote })
        steps.push({ parts: plus(base.text, ending, 'changed'), note: base.note as Lang })
    }
    else if (changed) {
        steps.push({ parts: [{ text: start, role: 'base' }, { text: ' → ', role: 'plus' }, { text: base.text, role: 'changed' }], note: base.note as Lang })
        steps.push(ending ? { parts: plus(base.text, ending, 'changed'), note: endingNote } : bare)
    }
    else {
        steps.push(ending ? { parts: plus(base.text, ending), note: endingNote } : bare)
    }

    steps.push({ parts: [{ text: form, role: 'ending' }], note: { hi: 'बना हुआ रूप', en: 'the finished form' } })

    return { result: form, steps, rule: rule ?? base.rule }
}

export const verbFormation = (root: string, rootClass: RootClass, tense: Tense, person: Person, num: Num, form: string, rule?: string): Formation => {
    const bases = verbBases(root, rootClass, tense, person)
    const cell = `${TENSE_LABEL[tense].hi}, ${PERSON_LABEL[person].hi} ${NUMBER_LABEL[num].hi}`
    const cellEn = `${TENSE_LABEL[tense].en}, ${PERSON_LABEL[person].hi} ${NUMBER_LABEL[num].en}`
    const base = bases.find(b => form.startsWith(b.text)) ?? { text: root }
    const ending = showEnding(form.slice(base.text.length))

    return build(root, form, bases, {
        hi: ending ? `${cell} का प्रत्यय -${ending.replace('◌', '')}` : cell,
        en: ending ? `ending -${ending.replace('◌', '')} for ${cellEn}` : cellEn
    }, rule)
}

export const kridantaFormation = (root: string, rootClass: RootClass, kind: KridantaKind, form: string, label: Lang, rule?: string): Formation => {
    const bases = verbBases(root, rootClass, `kr_${kind}`)
    const base = bases.find(b => form.startsWith(b.text)) ?? { text: root }
    const remainder = form.slice(base.text.length)
    const ending = ENDING_LABEL_OVERRIDE[remainder] ?? showEnding(remainder)

    return build(root, form, bases, {
        hi: `${label.hi} का प्रत्यय -${ending.replace('◌', '')}`,
        en: `${label.en}: ending -${ending.replace('◌', '')}`
    }, rule)
}

/** Stem + ending for a noun or pronoun form, naming lengthening/shortening of the final vowel. */
export const nounFormation = (stem: string, c: Case, num: Num, form: string, gender?: Gender, rule?: string): Formation => {
    const role: Lang = {
        hi: `${CASE_LABEL[c].hi} ${NUMBER_LABEL[num].hi}`,
        en: `${CASE_LABEL[c].en} ${NUMBER_LABEL[num].en}`
    }
    const steps: FormationStep[] = [{ parts: [{ text: stem, role: 'base' }], note: { hi: 'मूल शब्द', en: 'the stem' } }]
    const L = lengthen(stem)
    const S = shorten(stem)

    if (form === stem) {
        steps.push({ parts: [{ text: form, role: 'ending' }], note: { hi: `${role.hi}: मूल शब्द ही`, en: `${role.en}: the bare stem` } })

        return { result: form, steps, rule }
    }

    if (form.startsWith(stem)) {
        steps.push({ parts: plus(stem, showEnding(form.slice(stem.length))), note: { hi: `${role.hi} का चिह्न`, en: `${role.en} ending` } })
    }
    else if (L !== stem && form.startsWith(L)) {
        steps.push({ parts: [{ text: stem, role: 'base' }, { text: ' → ', role: 'plus' }, { text: L, role: 'changed' }], note: { hi: 'अन्तिम स्वर दीर्घ', en: 'the final vowel becomes long' } })

        if (form !== L) steps.push({ parts: plus(L, showEnding(form.slice(L.length)), 'changed'), note: { hi: `${role.hi} का चिह्न`, en: `${role.en} ending` } })
    }
    else if (S !== stem && form.startsWith(S)) {
        steps.push({ parts: [{ text: stem, role: 'base' }, { text: ' → ', role: 'plus' }, { text: S, role: 'changed' }], note: { hi: 'अन्तिम स्वर ह्रस्व', en: 'the final vowel becomes short' } })

        if (form !== S) steps.push({ parts: plus(S, showEnding(form.slice(S.length)), 'changed'), note: { hi: `${role.hi} का चिह्न`, en: `${role.en} ending` } })
    }
    else {
        const p = commonPrefix(stem, form)
        const tail = showEnding(stem.slice(p.length))
        const rem = showEnding(form.slice(p.length))

        steps.push({
            parts: plus(p || stem, rem),
            note: {
                hi: `अन्तिम ${tail.replace('◌', '')} के स्थान पर -${rem.replace('◌', '')} (${role.hi})`,
                en: `final ${tail.replace('◌', '')} is replaced by -${rem.replace('◌', '')} (${role.en})`
            }
        })
    }

    steps.push({ parts: [{ text: form, role: 'ending' }], note: { hi: gender ? 'बना हुआ रूप' : 'बना हुआ रूप', en: 'the finished form' } })

    return { result: form, steps, rule }
}
